import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoadingScreenProps {
  onComplete: () => void;
}

// Node y-positions for a small "neural network" (input -> hidden -> output)
const LAYER_X = [40, 160, 280];
const LAYERS: number[][] = [
  [30, 80, 130, 180],
  [10, 55, 100, 145, 190],
  [50, 100, 150],
];

const STATUS_MESSAGES = [
  "Wiring Samir's brain...",
  'Brewing coffee for the neurons...',
  'Untangling agentic thoughts...',
  'Teaching synapses new tricks...',
  'Double-checking the vibes...',
  'Almost ready...',
];

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + Math.random() * 12 + 6, 100));
    }, 220);

    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
    }, 480);

    const finishTimeout = setTimeout(() => {
      setProgress(100);
      setIsDone(true);
    }, 2600);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      clearTimeout(finishTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isDone) return;
    const exitTimeout = setTimeout(onComplete, 550);
    return () => clearTimeout(exitTimeout);
  }, [isDone, onComplete]);

  const connections = useMemo(() => {
    const lines: { x1: number; y1: number; x2: number; y2: number; delay: number }[] = [];

    LAYERS[0].forEach((y1, i) => {
      LAYERS[1].forEach((y2, j) => {
        lines.push({ x1: LAYER_X[0], y1, x2: LAYER_X[1], y2, delay: (i + j) * 0.06 });
      });
    });

    LAYERS[1].forEach((y1, i) => {
      LAYERS[2].forEach((y2, j) => {
        lines.push({ x1: LAYER_X[1], y1, x2: LAYER_X[2], y2, delay: 0.45 + (i + j) * 0.06 });
      });
    });

    return lines;
  }, []);

  return (
    <AnimatePresence>
      {!isDone && (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-dark-900"
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ duration: 0.55, ease: 'easeInOut' }}
        >
          {/* Dotted grid backdrop */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(59,130,246,0.5) 1px, transparent 0)',
              backgroundSize: '28px 28px',
            }}
          />

          {/* Ambient glow */}
          <div className="absolute w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse" />

          <motion.svg
            viewBox="0 0 320 210"
            className="w-64 h-44 md:w-80 md:h-56 relative z-10"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <defs>
              <linearGradient id="signalGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>

            {connections.map((c, idx) => (
              <motion.line
                key={idx}
                x1={c.x1}
                y1={c.y1}
                x2={c.x2}
                y2={c.y2}
                stroke="url(#signalGradient)"
                strokeWidth={0.8}
                initial={{ opacity: 0.05 }}
                animate={{ opacity: [0.05, 0.9, 0.05] }}
                transition={{
                  duration: 1.8,
                  delay: c.delay,
                  repeat: Infinity,
                  repeatType: 'mirror',
                  ease: 'easeInOut',
                }}
              />
            ))}

            {LAYERS.map((layer, layerIdx) =>
              layer.map((y, nodeIdx) => (
                <motion.circle
                  key={`${layerIdx}-${nodeIdx}`}
                  cx={LAYER_X[layerIdx]}
                  cy={y}
                  r={5}
                  fill={layerIdx === 1 ? '#60a5fa' : '#f8fafc'}
                  initial={{ scale: 0.8, opacity: 0.6 }}
                  animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.6, 1, 0.6] }}
                  transition={{
                    duration: 1.6,
                    delay: layerIdx * 0.3 + nodeIdx * 0.08,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))
            )}
          </motion.svg>

          <div className="mt-6 w-56 md:w-72 relative z-10">
            <div className="h-1.5 w-full bg-dark-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-primary-500 to-orange-400 rounded-full"
                style={{ width: `${progress}%` }}
                transition={{ ease: 'easeOut' }}
              />
            </div>
            <p className="mt-3 text-sm text-gray-400 font-mono text-center h-5">
              <AnimatePresence mode="wait">
                <motion.span
                  key={messageIndex}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.25 }}
                >
                  {STATUS_MESSAGES[messageIndex]}
                </motion.span>
              </AnimatePresence>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
